import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Clock, X, CreditCard, Shield, Lock, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const PaymentPage = () => {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  useEffect(() => {
    fetchPaymentLink();
  }, [linkId]);

  const fetchPaymentLink = async () => {
    try {
      const response = await axios.get(`${API}/payment-links/${linkId}`);
      setLink(response.data);
    } catch (error) {
      console.error('Error fetching payment link:', error);
      toast.error('Ödeme linki bulunamadı veya geçersiz');
    } finally {
      setLoading(false);
    }
  };

  const handleProceedToPayment = () => {
    // Redirect to Garanti BBVA payment form
    // API already includes /api, so we use it directly
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
    window.location.href = `${backendUrl}/api/payment/${linkId}/garanti-form`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!link) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <X className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Linki Bulunamadı</h1>
          <p className="text-gray-600">Bu ödeme linki geçersiz veya silinmiş olabilir.</p>
        </div>
      </div>
    );
  }

  if (link.status === 'paid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Tamamlandı</h1>
          <p className="text-gray-600 mb-4">Bu ödeme daha önce tamamlanmış.</p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Tutar:</span>
              <span className="font-semibold">{link.amount.toLocaleString('tr-TR')} {link.currency}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Ödeme Tarihi:</span>
              <span className="font-semibold">
                {new Date(link.paid_at).toLocaleDateString('tr-TR')}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (link.status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Linki Süresi Doldu</h1>
          <p className="text-gray-600 mb-4">Bu ödeme linkinin geçerlilik süresi dolmuş.</p>
          <p className="text-sm text-gray-500">Lütfen yeni bir ödeme linki için bizimle iletişime geçin.</p>
        </div>
      </div>
    );
  }

  // Check if expired
  const isExpired = new Date(link.expires_at) < new Date();
  if (isExpired && link.status === 'pending') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Ödeme Linki Süresi Doldu</h1>
          <p className="text-gray-600 mb-4">Bu ödeme linkinin geçerlilik süresi dolmuş.</p>
          <p className="text-sm text-gray-500">Son geçerlilik: {new Date(link.expires_at).toLocaleString('tr-TR')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <CreditCard className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Güvenli Ödeme
          </h1>
          <p className="text-gray-600">Ener Otomasyon Elektrik San. Tic. Ltd. Şti.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Ödeme Detayları</h2>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Alıcı</p>
                  <p className="font-medium text-gray-900">{link.customer_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Açıklama</p>
                  <p className="text-sm text-gray-900">{link.description}</p>
                </div>
                <div className="pt-3 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Tutar</span>
                    <span className="text-2xl font-bold text-gray-900">
                      {link.amount.toLocaleString('tr-TR')} {link.currency}
                    </span>
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Son geçerlilik: {new Date(link.expires_at).toLocaleString('tr-TR')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Info and Action */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <div className="flex items-center gap-2 mb-6">
                <Shield className="w-5 h-5 text-green-600" />
                <span className="text-sm text-gray-600">Garanti BBVA 3D Secure ile Korunmaktadır</span>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <Shield className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">Güvenli Ödeme</h3>
                      <p className="text-sm text-blue-800 mb-3">
                        Ödemeniz Garanti BBVA Sanal POS sistemi üzerinden 3D Secure güvenlik protokolü ile gerçekleştirilecektir.
                      </p>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          256-bit SSL şifreleme
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          3D Secure doğrulama
                        </li>
                        <li className="flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          PCI DSS uyumlu
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800 flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    <strong>Test Ortamı:</strong> Bu ödeme test ortamında gerçekleştirilecektir. Test kartları ile ödeme yapabilirsiniz.
                  </p>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Test Kartları</h4>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-2">
                    <div>
                      <p className="text-gray-600">Kart Numarası:</p>
                      <p className="font-mono font-semibold text-gray-900">5406 6975 4321 1173</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <p className="text-gray-600">Son Kullanma:</p>
                        <p className="font-mono font-semibold text-gray-900">03/23</p>
                      </div>
                      <div>
                        <p className="text-gray-600">CVV:</p>
                        <p className="font-mono font-semibold text-gray-900">465</p>
                      </div>
                      <div>
                        <p className="text-gray-600">İsim:</p>
                        <p className="font-semibold text-gray-900">Test User</p>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={handleProceedToPayment}
                  size="lg"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                  data-testid="pay-button"
                >
                  <Lock className="w-5 h-5 mr-2" />
                  Güvenli Ödeme Sayfasına Git
                  <ExternalLink className="w-5 h-5 ml-2" />
                </Button>
              </div>

              <div className="mt-6 flex items-center justify-center gap-4 text-xs text-gray-500">
                <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" className="h-6" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6" />
                <span className="font-semibold text-gray-600">Garanti BBVA</span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Info */}
        <div className="mt-8 text-center text-sm text-gray-600">
          <p className="flex items-center justify-center gap-2">
            <Shield className="w-4 h-4 text-green-600" />
            Ödemeniz SSL sertifikası ile korunmaktadır
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
